// src/views/Admin/Companies/components/DeleteCompanyModal/DeleteCompanyModal.tsx

import React, { useState } from 'react';
import { HiOutlineExclamationTriangle, HiOutlineXMark, HiOutlineBuildingStorefront } from 'react-icons/hi2';
import './DeleteCompanyModal.scss';

interface Local {
  local_id: number;
  local_name: string;
}

interface DeleteCompanyModalProps {
  isOpen: boolean;
  companyName: string;
  companyId: number;
  locales?: Local[];
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

const DeleteCompanyModal: React.FC<DeleteCompanyModalProps> = ({
  isOpen,
  companyName,
  companyId,
  locales = [],
  onClose,
  onConfirm
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const normalizeString = (str: string) => str.trim().normalize('NFC');
  const isConfirmEnabled = normalizeString(inputValue) === normalizeString(companyName);

  const handleConfirm = async () => {
    if (!isConfirmEnabled) return;
    
    setIsDeleting(true);
    setError(null);
    
    try {
      await onConfirm();
      setInputValue('');
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error al eliminar la compañía');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    if (isDeleting) return;
    setInputValue('');
    setError(null);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isConfirmEnabled && !isDeleting) {
      handleConfirm();
    }
    if (e.key === 'Escape') {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="delete-modal-overlay" onClick={handleClose}>
      <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={handleClose} disabled={isDeleting}>
          <HiOutlineXMark size={20} />
        </button>

        <div className="modal-icon">
          <HiOutlineExclamationTriangle size={32} />
        </div>

        <h2>Eliminar Compañía</h2>
        
        <div className="warning-box">
          <p><strong>¡Esta acción es irreversible!</strong></p>
          <p>Se eliminarán permanentemente todos los datos asociados a la compañía:</p>
          <ul>
            <li>Todos los locales y sus usuarios</li>
            <li>Permisos y configuraciones</li>
            <li>Pedidos, boletas y transacciones</li>
            <li>Menús y productos</li>
            <li>Proveedores y categorías</li>
            <li>Unidades de almacenamiento</li>
          </ul>
        </div>

        {locales.length > 0 && (
          <div className="locals-warning">
            <HiOutlineBuildingStorefront size={20} />
            <span>Esta compañía tiene <strong>{locales.length} local(es)</strong> que también serán eliminados.</span>
          </div>
        )}

        <div className="confirmation-section">
          <p>
            Para confirmar, escribe <strong>"{companyName}"</strong> a continuación:
          </p>
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={`Escribe "${companyName}" para confirmar`}
            disabled={isDeleting}
            autoFocus
          />
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="modal-actions">
          <button 
            className="cancel-button" 
            onClick={handleClose}
            disabled={isDeleting}
          >
            Cancelar
          </button>
          <button 
            className="delete-button"
            onClick={handleConfirm}
            disabled={!isConfirmEnabled || isDeleting}
          >
            {isDeleting ? (
              <>
                <span className="spinner" />
                Eliminando...
              </>
            ) : (
              'Eliminar Permanentemente'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteCompanyModal;
